import React, { useState, useMemo, useEffect, useCallback } from 'react';
import './CircleTab.css';

// =====================================================================
// CIRCLE TAB — Social Circle + TheoUniverse map + Level-Up Pyramid
// Reads from user (login): circleRing, circlePlanet, pyramidTier, pyramidLevel, memberships
// Loads from API: companies (moons) + people (only for confirmed members)
// Writes nothing.
// =====================================================================

const UNIVERSE_API = 'https://orkxz4jpah.execute-api.us-east-1.amazonaws.com/get-universe';
const PUBLIC_URL = (typeof process !== 'undefined' && process.env && process.env.PUBLIC_URL) || '';
const logoUrl = (companyId) => `${PUBLIC_URL}/logos/companies/${companyId}.png`;

const C = 320;
const MARKER_ANGLE = 90; // низ кола — там немає планет

const PLANETS = [
  { id: 'technology',  name: 'Technology',             sub: '',                             rank: 1, orbit: 178, angle: 135,  color: '#d8322e', label: ['Technology'] },
  { id: 'education',   name: 'Education',              sub: 'Academics & Entrepreneurship', rank: 2, orbit: 178, angle: 45,   color: '#5fa133', label: ['Education'] },
  { id: 'health',      name: 'Health & Wellness',      sub: 'Oxygen, water, nutrition',     rank: 3, orbit: 178, angle: -135, color: '#17a98c', label: ['Health &', 'Wellness'] },
  { id: 'travel',      name: 'Travel & Entertainment', sub: 'Fashion & Lifestyle',          rank: 4, orbit: 178, angle: -45,  color: '#e0a020', label: ['Travel &', 'Entertain-', 'ment'] },
  { id: 'media',       name: 'Media',                  sub: '', rank: 0, orbit: 226, angle: -110, color: '#e3677b', label: ['Media'] },
  { id: 'gaming',      name: 'Gaming',                 sub: '', rank: 0, orbit: 226, angle: -70,  color: '#2d6bd8', label: ['Gaming'] },
  { id: 'banking',     name: 'Banking & Finance',      sub: '', rank: 0, orbit: 226, angle: 0,    color: '#e8771c', label: ['Banking', '& Finance'] },
  { id: 'global',      name: 'Global Initiatives',     sub: '', rank: 0, orbit: 226, angle: 70,   color: '#8b3fd8', label: ['Global', 'Initiatives'] },
  { id: 'agriculture', name: 'Agriculture',            sub: '', rank: 0, orbit: 226, angle: 110,  color: '#c9a227', label: ['Agri-', 'culture'] },
  { id: 'realestate',  name: 'Real Estate & Construction', sub: '', rank: 0, orbit: 226, angle: 180, color: '#2f5fe0', label: ['Real Estate', '& Constr-', 'uction'] },
];
const planetById = (id) => PLANETS.find((p) => p.id === id);

const DEPTS = [
  { id: 'leadership', name: 'Leadership', note: 'Director / PM', color: '#e3b23c' },
  { id: 'sales', name: 'Sales & Marketing', note: '', color: '#e3677b' },
  { id: 'operations', name: 'Operations', note: '', color: '#2f8fc4' },
  { id: 'finance', name: 'Finance', note: '', color: '#5fa133' },
  { id: 'hr', name: 'HR & Admin', note: '', color: '#8b3fd8' },
  { id: 'legal', name: 'Legal', note: '', color: '#6d7079' },
  { id: 'rnd', name: 'R&D', note: '', color: '#d8322e' },
];
const deptById = (id) => DEPTS.find((d) => d.id === id);

const OUTER_TEXT = 'Status not confirmed. New members start here after registration and have no access inward yet. They move in when confirmed manually or by the pyramid algorithm.';

const RINGS = {
  god:     { title: 'God – Father', eyebrow: 'Center of the universe', text: 'Always at the center. Shown as a fact; nobody is assigned here.', fact: true },
  sam:     { title: 'Sam Stathis', eyebrow: 'Circle 2', text: 'Sotirios Hristos Stathopoulos. Founder of Theometrics.', r: 66, people: true },
  mother:  { title: 'Mother', eyebrow: 'Circle 3', text: 'Shown as a fact; nobody is assigned here.', fact: true },
  semigod: { title: 'Semi Gods', eyebrow: 'Circle 4', text: 'Names are not shown on the map. Members of this orbit are listed below.', r: 106, people: true },
  angel:   { title: 'Angels', eyebrow: 'Circle 5', text: 'Names are not shown on the map. Members of this orbit are listed below.', r: 126, people: true },
  planet:  { title: 'Planet member', eyebrow: 'Circle 6', text: 'Every member of the sixth circle belongs to one planet of the THEO universe.' },
  outer:   { title: 'Curious Souls · Wanderers', eyebrow: 'Outer orbit', text: OUTER_TEXT, r: 262, unconfirmed: true, people: true },
  spirit:  { title: 'Spirituality', eyebrow: 'Outer boundary', text: 'The boundary of the THEO universe.', fact: true },
};
const ASSIGNABLE = ['sam', 'semigod', 'angel', 'planet', 'outer'];

const TIER_COLORS = { 4: '#6d7079', 3: '#b8743a', 2: '#c3c8d0', 1: '#d9a93a' };

// ---------------- helpers ----------------
const rad = (d) => (d * Math.PI) / 180;
const pt = (r, deg) => [C + r * Math.cos(rad(deg)), C + r * Math.sin(rad(deg))];
const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
const band = (r, w, color) => `<circle cx="${C}" cy="${C}" r="${r}" fill="none" stroke="${color}" stroke-width="${w}"/>`;
const repeatTxt = (words, n) => Array.from({ length: n }, (_, i) => words[i % words.length]).join(' · ') + ' · ';
const initials = (name) => String(name || '?').split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
const wrapWords = (txt, max, maxLines = 3) => {
  const lines = [''];
  String(txt).split(' ').forEach((w) => {
    const cur = lines[lines.length - 1];
    if (cur && (cur + ' ' + w).length > max) lines.push(w);
    else lines[lines.length - 1] = (cur + ' ' + w).trim();
  });
  return lines.slice(0, maxLines);
};

function shade(hex, f) {
  const n = parseInt(hex.slice(1), 16);
  const m = (c) => Math.max(0, Math.min(255, Math.round(f >= 0 ? c + (255 - c) * f : c * (1 + f))));
  return `rgb(${m(n >> 16)},${m((n >> 8) & 255)},${m(n & 255)})`;
}

function fullTextRing(id, r, txt, color, size) {
  const [x0, y0] = pt(r, -90), [x1, y1] = pt(r, 90);
  const d = `M${x0},${y0} A${r},${r} 0 1,1 ${x1},${y1} A${r},${r} 0 1,1 ${x0},${y0}`;
  const len = 2 * Math.PI * r;
  return `<path id="${id}" d="${d}" fill="none"/>
    <text font-family="Cinzel, Georgia, serif" font-weight="700" font-size="${size}" fill="${color}" dominant-baseline="central">
      <textPath href="#${id}" textLength="${(len - 3).toFixed(1)}" lengthAdjust="spacing">${esc(txt)}</textPath></text>`;
}

// одне слово, розтягнуте дугою: 'top' — зверху, 'bottom' — знизу (обидва читаються зліва направо)
function arcText(id, r, txt, color, size, spanDeg, via = 'top', weight = 700) {
  const half = spanDeg / 2;
  const a0 = via === 'top' ? -90 - half : 90 + half;
  const a1 = via === 'top' ? -90 + half : 90 - half;
  const [x0, y0] = pt(r, a0), [x1, y1] = pt(r, a1);
  const d = `M${x0.toFixed(2)},${y0.toFixed(2)} A${r},${r} 0 0,${via === 'top' ? 1 : 0} ${x1.toFixed(2)},${y1.toFixed(2)}`;
  const len = (Math.PI * r * spanDeg) / 180;
  return `<path id="${id}" d="${d}" fill="none"/>
    <text font-family="Cinzel, Georgia, serif" font-weight="${weight}" font-size="${size}" fill="${color}" dominant-baseline="central">
      <textPath href="#${id}" textLength="${len.toFixed(1)}" lengthAdjust="spacing">${esc(txt)}</textPath></text>`;
}

function meander(r0, r1, n, color) {
  const seg = 360 / n;
  const key = [[0, 0], [0, 1], [0.82, 1], [0.82, 0.22], [0.3, 0.22], [0.3, 0.62], [0.58, 0.62]];
  let out = band(r0, 2, color);
  for (let i = 0; i < n; i++) {
    const pts = key.map(([u, v]) => pt(r0 + v * (r1 - r0), i * seg + u * seg).map((k) => k.toFixed(1)).join(',')).join(' ');
    out += `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="2.2"/>`;
  }
  return out;
}

function stars(w, h, count, seed) {
  let s = seed, out = '';
  const rnd = () => (s = (s * 16807) % 2147483647) / 2147483647;
  for (let i = 0; i < count; i++) {
    out += `<circle cx="${(rnd() * w).toFixed(1)}" cy="${(rnd() * h).toFixed(1)}" r="${(0.4 + rnd() * 1.1).toFixed(2)}" fill="#fff" opacity="${(0.25 + rnd() * 0.6).toFixed(2)}"/>`;
  }
  return out;
}

// ---------------- Social Circle SVG ----------------
function planetSvg(p) {
  const [x, y] = pt(p.orbit, p.angle);
  const lines = p.rank ? [`#${p.rank}`, ...p.label] : p.label;
  const lh = 10.5;
  const y0 = -((lines.length - 1) * lh) / 2;
  const tsp = lines
    .map((l, i) => `<tspan x="0" y="${(y0 + i * lh).toFixed(1)}" ${p.rank && i === 0 ? 'font-size="11" fill="#ffe7a3"' : ''}>${esc(l)}</tspan>`)
    .join('');
  return `<g class="ct-hit" tabindex="0" role="button" aria-label="${esc(p.name)}" data-kind="planet" data-id="${p.id}" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
    <circle r="33" fill="${p.color}" opacity=".16"/>
    <circle class="ct-focusring" r="31" fill="none" stroke="${p.color}" stroke-width="1.6" opacity=".9" filter="url(#ct-glow)"/>
    <circle r="28" fill="${p.color}"/>
    <circle r="28" fill="url(#ct-shine)"/>
    <circle r="28" fill="url(#ct-shade)"/>
    <text text-anchor="middle" dominant-baseline="central" font-family="-apple-system, Segoe UI, Roboto, sans-serif" font-weight="800" font-size="9.5" fill="#fff" stroke="rgba(10,8,25,.75)" stroke-width="2.6" paint-order="stroke">${tsp}</text>
  </g>`;
}

const ringHit = (kind, rIn, rOut, label) =>
  `<circle class="ct-hit" tabindex="0" role="button" aria-label="${esc(label)}" data-kind="ring" data-id="${kind}" cx="${C}" cy="${C}" r="${(rIn + rOut) / 2}" fill="none" stroke="transparent" stroke-width="${rOut - rIn}" pointer-events="stroke"/>`;

function markerSvg(ring, planetId) {
  let x, y, ringR;
  if (ring === 'planet') {
    const p = planetById(planetId);
    [x, y] = pt(p.orbit, p.angle);
    ringR = 35;
  } else {
    [x, y] = pt(RINGS[ring].r, MARKER_ANGLE);
    ringR = 11;
  }
  const pillW = 46, pillY = ring === 'planet' ? -ringR - 13 : -26;
  return `<g pointer-events="none" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
    <circle class="ct-pulse" r="${ringR}" fill="none" stroke="#fff" stroke-width="3"/>
    <circle r="${ringR}" fill="none" stroke="#e5262b" stroke-width="3"/>
    ${ring === 'planet' ? '' : '<circle r="6" fill="#fff" stroke="#e5262b" stroke-width="3"/>'}
    <g transform="translate(0,${pillY})">
      <rect x="${-pillW / 2}" y="-10" width="${pillW}" height="20" rx="10" fill="#e5262b" stroke="#fff" stroke-width="1.5"/>
      <text text-anchor="middle" dominant-baseline="central" font-family="-apple-system, Segoe UI, Roboto, sans-serif" font-weight="800" font-size="11" fill="#fff" letter-spacing="1">YOU</text>
    </g>
  </g>`;
}

function buildCircleSvg(ring, planetId) {
  return `
    <defs>
      <radialGradient id="ct-shine" cx="35%" cy="30%" r="60%"><stop offset="0" stop-color="#fff" stop-opacity=".7"/><stop offset=".45" stop-color="#fff" stop-opacity=".08"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>
      <radialGradient id="ct-shade" cx="50%" cy="50%" r="50%"><stop offset=".6" stop-color="#000" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".55"/></radialGradient>
      <radialGradient id="ct-sun" cx="45%" cy="40%" r="62%"><stop offset="0" stop-color="#fffbe6"/><stop offset=".35" stop-color="#ffe27a"/><stop offset=".75" stop-color="#ffb21e"/><stop offset="1" stop-color="#f07a12"/></radialGradient>
      <radialGradient id="ct-corona" cx="50%" cy="50%" r="50%"><stop offset=".55" stop-color="#ffcf4a" stop-opacity=".55"/><stop offset="1" stop-color="#ff9f1a" stop-opacity="0"/></radialGradient>
      <filter id="ct-sunglow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="5"/></filter>
      <filter id="ct-glow" x="-50%" y="-50%" width="200%" height="200%"><feGaussianBlur stdDeviation="2.4" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
    </defs>
    ${stars(640, 640, 110, 7)}

    ${band(305, 14, '#c4161c')}
    <g class="ct-spin-slow">${fullTextRing('ct-tp-spirit', 305, repeatTxt(['SPIRITUALITY'], 9), '#fff', 9)}</g>
    ${meander(281, 296, 44, '#e3b23c')}

    ${band(262, 16, '#2e2530')}
    ${band(254, 1, 'rgba(233,201,166,.5)')}${band(270, 1, 'rgba(233,201,166,.5)')}
    <g class="ct-spin-rev">${fullTextRing('ct-tp-outer', 262, repeatTxt(['CURIOUS SOULS', 'WANDERERS'], 10), '#e9c9a6', 9)}</g>

    ${band(226, 1, 'rgba(255,255,255,.14)')}
    ${band(178, 1, 'rgba(255,255,255,.14)')}

    ${band(126, 16, '#2f8fc4')}
    ${band(117.5, 2, '#e3b23c')}${band(134.5, 2, '#e3b23c')}
    ${arcText('ct-tp-angel', 126, 'ANGELS', '#fff', 11, 70)}

    ${band(106, 16, '#8a5328')}
    ${band(98.5, 1.5, '#5e3517')}${band(113.5, 1.5, '#5e3517')}
    ${arcText('ct-tp-semi', 106, 'SEMI GODS', '#f6dfc2', 11, 90)}

    ${band(86, 16, '#c9ced6')}
    ${arcText('ct-tp-mother', 86, 'MOTHER', '#24212f', 11, 80)}

    ${band(66, 16, '#e3b23c')}
    ${arcText('ct-tp-sam', 66, 'SAM STATHIS', '#24212f', 12.5, 115, 'top', 900)}
    ${arcText('ct-tp-sam2', 66, 'SOTIRIOS HRISTOS STATHOPOULOS', '#4a3a10', 7.4, 168, 'bottom')}

    <circle cx="${C}" cy="${C}" r="57" fill="url(#ct-corona)" filter="url(#ct-sunglow)"/>
    <circle cx="${C}" cy="${C}" r="44" fill="url(#ct-sun)"/>
    <text x="${C}" y="${C - 7}" text-anchor="middle" dominant-baseline="central" font-family="Cinzel, Georgia, serif" font-weight="900" font-size="14" fill="#6b2a00">GOD</text>
    <text x="${C}" y="${C + 9}" text-anchor="middle" dominant-baseline="central" font-family="Cinzel, Georgia, serif" font-weight="900" font-size="11" fill="#6b2a00">– FATHER –</text>

    ${ringHit('spirit', 297, 320, 'Spirituality')}
    ${ringHit('outer', 254, 270, 'Curious Souls and Wanderers')}
    ${ringHit('angel', 118, 134, 'Angels')}
    ${ringHit('semigod', 98, 114, 'Semi Gods')}
    ${ringHit('mother', 78, 94, 'Mother')}
    ${ringHit('sam', 58, 74, 'Sam Stathis')}
    <circle class="ct-hit" tabindex="0" role="button" aria-label="God – Father" data-kind="ring" data-id="god" cx="${C}" cy="${C}" r="50" fill="transparent"/>

    ${PLANETS.map(planetSvg).join('')}
    ${markerSvg(ring, planetId)}
  `;
}

// ---------------- Planet sheet SVGs ----------------
const MOON_DEFS = `
  <radialGradient id="ct-moon" cx="35%" cy="30%" r="70%"><stop offset="0" stop-color="#fff"/><stop offset=".45" stop-color="#c9ced6"/><stop offset="1" stop-color="#5d6370"/></radialGradient>
  <radialGradient id="ct-sat-shine" cx="35%" cy="30%" r="60%"><stop offset="0" stop-color="#fff" stop-opacity=".6"/><stop offset="1" stop-color="#fff" stop-opacity="0"/></radialGradient>
  <clipPath id="ct-clip18"><circle r="18"/></clipPath>
  <clipPath id="ct-clip34"><circle r="34"/></clipPath>`;

// місяць-компанія: срібна куля + логотип зверху (тільки якщо PNG реально завантажився)
const moonSvg = (companyId, r, logos) => `
  <circle r="${r}" fill="url(#ct-moon)"/>
  ${logos.has(companyId) ? `<image href="${esc(logoUrl(companyId))}" x="${-r}" y="${-r}" width="${2 * r}" height="${2 * r}" preserveAspectRatio="xMidYMid slice" clip-path="url(#ct-clip${r})"/>` : ''}`;

function buildPlanetView(planet, companies, myCompanyIds, logos) {
  const cx = 180, cy = 128, rx = 132, ry = 84;
  const moons = companies.map((c, i) => {
    const a = rad(-90 + (i * 360) / companies.length);
    const x = cx + rx * Math.cos(a), y = cy + ry * Math.sin(a);
    const lines = wrapWords(c.name, 14).map((l, k) => `<tspan x="0" dy="${k ? 11 : 0}">${esc(l)}</tspan>`).join('');
    return `<g class="ct-hit" data-company="${esc(c.companyId)}" tabindex="0" role="button" aria-label="${esc(c.name)}" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
      ${myCompanyIds.includes(c.companyId) ? '<circle r="24" fill="none" stroke="#e5262b" stroke-width="2.5"/>' : ''}
      ${moonSvg(c.companyId, 18, logos)}
      <text y="31" text-anchor="middle" font-family="-apple-system, Segoe UI, sans-serif" font-weight="700" font-size="10" fill="#fff" stroke="rgba(10,8,25,.85)" stroke-width="2.5" paint-order="stroke">${lines}</text>
    </g>`;
  }).join('');
  return `<svg viewBox="0 0 360 270" role="img" aria-label="${esc(planet.name)} and its companies">
    <defs>${MOON_DEFS}
      <radialGradient id="ct-pl" cx="35%" cy="30%" r="70%"><stop offset="0" stop-color="#fff" stop-opacity=".85"/><stop offset=".35" stop-color="${planet.color}"/><stop offset="1" stop-color="#000" stop-opacity=".85"/></radialGradient></defs>
    ${stars(360, 270, 30, 5)}
    <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="rgba(255,255,255,.18)" stroke-dasharray="3 4"/>
    <circle cx="${cx}" cy="${cy}" r="44" fill="${planet.color}" opacity=".18"/>
    <circle cx="${cx}" cy="${cy}" r="36" fill="url(#ct-pl)"/>
    <text x="${cx}" y="${cy}" text-anchor="middle" dominant-baseline="central" font-family="Cinzel, Georgia, serif" font-weight="700" font-size="${planet.name.length > 12 ? 8 : 11}" fill="#fff" stroke="rgba(10,8,25,.6)" stroke-width="2" paint-order="stroke">${esc(planet.name.toUpperCase())}</text>
    ${companies.length ? moons : `<text x="${cx}" y="${cy + 72}" text-anchor="middle" font-family="-apple-system, sans-serif" font-size="12" fill="#c9c3dc">No companies yet</text>`}
  </svg>`;
}

function buildCompanyView(company, planet, counts, myDepts, showCounts, logos) {
  const cx = 180, cy = 125, rx = 132, ry = 86;
  const sats = DEPTS.map((d, i) => {
    const a = rad(-90 + (i * 360) / DEPTS.length);
    const x = cx + rx * Math.cos(a), y = cy + ry * Math.sin(a);
    return `<g class="ct-hit" data-dept="${d.id}" tabindex="0" role="button" aria-label="${esc(d.name)}" transform="translate(${x.toFixed(1)},${y.toFixed(1)})">
      ${myDepts.includes(d.id) ? '<circle r="21" fill="none" stroke="#e5262b" stroke-width="2.5"/>' : ''}
      <circle r="15" fill="${d.color}"/><circle r="15" fill="url(#ct-sat-shine)"/>
      <text text-anchor="middle" dominant-baseline="central" font-family="-apple-system, sans-serif" font-weight="800" font-size="11" fill="#fff">${showCounts ? counts[d.id] || 0 : '·'}</text>
      <text y="27" text-anchor="middle" font-family="-apple-system, Segoe UI, sans-serif" font-weight="700" font-size="10" fill="#fff" stroke="rgba(10,8,25,.85)" stroke-width="2.5" paint-order="stroke">${esc(d.name)}</text>
    </g>`;
  }).join('');
  return `<svg viewBox="0 0 360 260" role="img" aria-label="${esc(company.name)} departments">
    <defs>${MOON_DEFS}</defs>
    ${stars(360, 260, 30, 9)}
    <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}" fill="none" stroke="rgba(255,255,255,.18)" stroke-dasharray="3 4"/>
    <circle cx="${cx}" cy="${cy}" r="42" fill="${planet.color}" opacity=".18"/>
    <g transform="translate(${cx},${cy})">${moonSvg(company.companyId, 34, logos)}</g>
    ${sats}
  </svg>`;
}

// ---------------- Pyramid SVG ----------------
function buildPyramidSvg(tier, level) {
  const cx = 200, W = 150, K = 0.42, H = 13, STEPS = 16, TIER_GAP = 3;
  const activeIdx = (4 - tier) * 4 + (level - 1); // 0 = Tier 4 Level 1 (низ)
  let frontY = 360, prevTop = null;
  let out = stars(400, 390, 40, 3);
  const tierMid = {};
  let markerAt = null;
  const poly = (a) => a.map((q) => q.map((n) => n.toFixed(1)).join(',')).join(' ');
  for (let i = 0; i < STEPS; i++) {
    const t = 4 - Math.floor(i / 4), lvl = (i % 4) + 1;
    const s = W * (1 - i / (STEPS + 1));
    if (prevTop !== null) frontY = prevTop + s * K + (i % 4 === 0 ? -TIER_GAP : 0);
    const top = frontY - H;
    const L = [cx - s, frontY - s * K], F = [cx, frontY], R = [cx + s, frontY - s * K];
    const Lt = [L[0], L[1] - H], Ft = [cx, top], Rt = [R[0], R[1] - H], Bt = [cx, top - 2 * s * K];
    const base = TIER_COLORS[t];
    const isActive = i === activeIdx, above = i > activeIdx;
    const bright = isActive ? 0.18 : 0;
    out += `<g class="ct-hit" tabindex="0" role="button" aria-label="Tier ${t} Level ${lvl}" data-tier="${t}" data-level="${lvl}" opacity="${above ? 0.38 : 1}" ${isActive ? 'filter="url(#ct-pglow)"' : ''}>
      <polygon points="${poly([Lt, Ft, Rt, Bt])}" fill="${shade(base, 0.35 + bright)}"/>
      <polygon points="${poly([L, F, Ft, Lt])}" fill="${shade(base, 0.08 + bright)}"/>
      <polygon points="${poly([F, R, Rt, Ft])}" fill="${shade(base, -0.28 + bright)}"/>
      <polyline class="ct-focusring" points="${poly([L, F, R])}" fill="none" stroke="${isActive ? '#fff' : 'rgba(0,0,0,.35)'}" stroke-width="${isActive ? 1.6 : 0.8}"/>
      ${isActive ? `<polygon points="${poly([L, F, R, Rt, Ft, Lt])}" fill="none" stroke="#fff" stroke-width="1.6"/>` : ''}
    </g>`;
    if (lvl === 1) tierMid[t] = L[1] - H / 2;
    if (lvl === 4) tierMid[t] = (tierMid[t] + L[1] - H / 2) / 2;
    if (isActive) markerAt = [R[0], R[1] - H / 2];
    prevTop = top - s * K;
  }
  let labels = '';
  [4, 3, 2, 1].forEach((t) => {
    labels += `<text x="14" y="${(tierMid[t] + 4).toFixed(1)}" font-family="Cinzel, Georgia, serif" font-weight="700" font-size="12" fill="${shade(TIER_COLORS[t], 0.25)}" letter-spacing="1.5">TIER ${t}</text>`;
  });
  const [mx, my] = markerAt;
  const pillX = 312, pillW = 76;
  const marker = `<g pointer-events="none">
    <line x1="${(mx + 4).toFixed(1)}" y1="${my.toFixed(1)}" x2="${pillX - pillW / 2}" y2="${my.toFixed(1)}" stroke="#fff" stroke-width="1.4" stroke-dasharray="3 3"/>
    <circle cx="${mx.toFixed(1)}" cy="${my.toFixed(1)}" r="5" fill="#fff"/>
    <circle class="ct-pulse" cx="${mx.toFixed(1)}" cy="${my.toFixed(1)}" r="9" fill="none" stroke="#fff" stroke-width="2"/>
    <rect x="${pillX - pillW / 2}" y="${(my - 12).toFixed(1)}" width="${pillW}" height="24" rx="12" fill="#e5262b" stroke="#fff" stroke-width="1.5"/>
    <text x="${pillX}" y="${(my + 1).toFixed(1)}" text-anchor="middle" dominant-baseline="central" font-family="-apple-system, Segoe UI, Roboto, sans-serif" font-weight="800" font-size="11" fill="#fff">YOU · T${tier} L${level}</text>
  </g>`;
  return `<defs><filter id="ct-pglow" x="-30%" y="-80%" width="160%" height="260%"><feGaussianBlur stdDeviation="4" result="b"/><feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge></filter></defs>
    <text x="200" y="26" text-anchor="middle" font-family="Cinzel, Georgia, serif" font-weight="700" font-size="15" fill="#fff" letter-spacing="3">LEVEL UP</text>
    ${out}${labels}${marker}`;
}

// ---------------- data normalisation ----------------
function readPosition(user) {
  let ring = user?.circleRing;
  if (!ASSIGNABLE.includes(ring)) ring = 'outer';
  const planet = user?.circlePlanet;
  if (ring === 'planet' && !planetById(planet)) ring = 'outer';
  const clamp = (v, d) => {
    const n = parseInt(v, 10);
    return n >= 1 && n <= 4 ? n : d;
  };
  return { ring, planet, tier: clamp(user?.pyramidTier, 4), level: clamp(user?.pyramidLevel, 1) };
}
const normMemberships = (list) =>
  (Array.isArray(list) ? list : []).filter((m) => m && m.companyId && deptById(m.position));

// ---------------- small UI pieces ----------------
function Avatar({ person, size = 34 }) {
  const [broken, setBroken] = useState(false);
  const style = { width: size, height: size };
  if (person.photoUrl && !broken) {
    return <img className="ct-ava" style={style} src={person.photoUrl} alt="" onError={() => setBroken(true)} />;
  }
  return <div className="ct-ava" style={{ ...style, background: '#4f46e5' }}>{initials(person.name)}</div>;
}

function CompanyLogo({ companyId, size = 32 }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="ct-moonicon" style={{ width: size, height: size }}>
      {!broken && <img src={logoUrl(companyId)} alt="" onError={() => setBroken(true)} />}
    </div>
  );
}

function PersonRow({ person, meId, highlight, onAction, subtitle }) {
  const isMe = person.genesisID === meId;
  return (
    <div className={`ct-person ${highlight ? 'hl' : ''}`}>
      <Avatar person={person} />
      <div className="ct-who">
        <b>{person.name}{isMe && <span className="ct-you">YOU</span>}</b>
        <small>{subtitle || `Genesis ID ${person.genesisID}`}</small>
      </div>
      {/* Contact — повернеться разом із мінімесенджером */}
      <button onClick={() => onAction(person)}>Profile</button>
    </div>
  );
}

// ---------------- component ----------------
function CircleTab({ user, assets = [], onSendToken, onTransferAsset }) {
  const pos = readPosition(user);
  const meId = user?.genesisID;
  const myMemberships = normMemberships(user?.memberships);

  const [circleFocus, setCircleFocus] = useState(null);
  const [pyrFocus, setPyrFocus] = useState(null);
  const [note, setNote] = useState('');
  const [universe, setUniverse] = useState({ loading: true, error: '', confirmed: false, companies: [], people: [] });
  const [sheet, setSheet] = useState(null); // { planet, company, openDept, highlight }
  const [query, setQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  // ----- load universe -----
  const loadUniverse = useCallback(async () => {
    if (!meId) return;
    setUniverse((u) => ({ ...u, loading: true, error: '' }));
    try {
      const res = await fetch(UNIVERSE_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ genesisID: meId }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Failed to load');
      setUniverse({
        loading: false, error: '', confirmed: !!data.confirmed,
        companies: data.companies || [],
        people: (data.people || []).map((p) => ({ ...p, memberships: normMemberships(p.memberships) })),
      });
    } catch (e) {
      console.error('get-universe error:', e);
      setUniverse((u) => ({ ...u, loading: false, error: e.message }));
    }
  }, [meId]);

  useEffect(() => { loadUniverse(); }, [loadUniverse]);

  // які логотипи реально існують (щоб на орбіті не було "битих" картинок)
  const [logos, setLogos] = useState(() => new Set());
  useEffect(() => {
    let alive = true;
    universe.companies.forEach((c) => {
      const img = new Image();
      img.onload = () => { if (alive) setLogos((prev) => (prev.has(c.companyId) ? prev : new Set(prev).add(c.companyId))); };
      img.src = logoUrl(c.companyId);
    });
    return () => { alive = false; };
  }, [universe.companies]);

  useEffect(() => {
    if (!sheet) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setSheet(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [sheet]);

  // ----- derived -----
  const companyById = useCallback((id) => universe.companies.find((c) => c.companyId === id), [universe.companies]);
  const membersOf = useCallback(
    (cid, did) => universe.people.filter((p) => p.memberships.some((m) => m.companyId === cid && (!did || m.position === did))),
    [universe.people]
  );
  const myCompanyIds = myMemberships.map((m) => m.companyId);
  const myDeptsIn = (cid) => myMemberships.filter((m) => m.companyId === cid).map((m) => m.position);

  const circleHtml = useMemo(() => buildCircleSvg(pos.ring, pos.planet), [pos.ring, pos.planet]);
  const pyramidHtml = useMemo(() => buildPyramidSvg(pos.tier, pos.level), [pos.tier, pos.level]);

  const openPlanet = (pid) => setSheet({ planet: pid, company: null, openDept: null, highlight: null });

  const activateFrom = (e, handler) => {
    if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
    const g = e.target.closest && e.target.closest('.ct-hit');
    if (!g) return;
    if (e.type === 'keydown') e.preventDefault();
    handler(g);
  };
  const onCircle = (e) => activateFrom(e, (g) => {
    setCircleFocus({ kind: g.dataset.kind, id: g.dataset.id });
    setNote('');
    if (g.dataset.kind === 'planet') openPlanet(g.dataset.id);
  });
  const onPyramid = (e) => activateFrom(e, (g) => setPyrFocus({ tier: +g.dataset.tier, level: +g.dataset.level }));
  const onSheetVisual = (e) => activateFrom(e, (g) => {
    if (g.dataset.company) setSheet((s) => ({ ...s, company: g.dataset.company, openDept: null, highlight: null }));
    else if (g.dataset.dept) setSheet((s) => ({ ...s, openDept: g.dataset.dept }));
  });

  // ----- person card -----
  const [personCard, setPersonCard] = useState(null); // { person, mode: null | 'choose' | 'asset' }
  const personAction = (person) => setPersonCard({ person, mode: null });
  const canSend = !!(onSendToken || onTransferAsset);
  const closeAll = () => { setPersonCard(null); setSheet(null); };
  const sendToken = (symbol, person) => { closeAll(); onSendToken(symbol, String(person.genesisID)); };
  const sendAsset = (asset, person) => { closeAll(); onTransferAsset(asset, String(person.genesisID)); };

  // ----- search -----
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    const out = [];
    universe.companies
      .filter((c) => c.name.toLowerCase().includes(q))
      .forEach((c) => out.push({ kind: 'company', key: 'c-' + c.companyId, company: c }));
    if (universe.confirmed) {
      universe.people
        .filter((p) => p.name.toLowerCase().includes(q) || String(p.genesisID).includes(q))
        .forEach((p) => {
          if (p.memberships.length) {
            p.memberships.slice(0, 3).forEach((m) => out.push({ kind: 'member', key: `m-${p.genesisID}-${m.companyId}-${m.position}`, person: p, m }));
          } else {
            out.push({ kind: 'ring', key: 'r-' + p.genesisID, person: p });
          }
        });
    }
    return out.slice(0, 8);
  }, [query, universe]);

  const pickResult = (r) => {
    setShowResults(false);
    if (r.kind === 'company') {
      setSheet({ planet: r.company.planetId, company: r.company.companyId, openDept: null, highlight: null });
    } else if (r.kind === 'member') {
      const c = companyById(r.m.companyId);
      if (c) setSheet({ planet: c.planetId, company: c.companyId, openDept: r.m.position, highlight: r.person.genesisID });
    } else if (r.person.circleRing === 'planet' && planetById(r.person.circlePlanet)) {
      setSheet({ planet: r.person.circlePlanet, company: null, openDept: null, highlight: r.person.genesisID });
    } else {
      setCircleFocus({ kind: 'ring', id: r.person.circleRing });
    }
  };

  const resultPath = (r) => {
    if (r.kind === 'company') return `${planetById(r.company.planetId)?.name || ''} · company`;
    if (r.kind === 'member') {
      const c = companyById(r.m.companyId);
      return `${planetById(c?.planetId)?.name || ''} › ${c?.name || r.m.companyId} › ${deptById(r.m.position).name}`;
    }
    if (r.person.circleRing === 'planet') return `${planetById(r.person.circlePlanet)?.name || ''} · planet member`;
    return RINGS[r.person.circleRing]?.title || '';
  };

  // ----- circle info card -----
  const f = circleFocus;
  const focusPlanet = f ? (f.kind === 'planet' ? planetById(f.id) : null) : (pos.ring === 'planet' ? planetById(pos.planet) : null);
  const key = f ? (f.kind === 'planet' ? 'planet' : f.id) : pos.ring;
  const r = RINGS[key];
  const mine = !f || (f.kind === 'planet' ? pos.ring === 'planet' && pos.planet === f.id : f.id === pos.ring);
  let title = r.title, text = r.text;
  if (focusPlanet) {
    title = focusPlanet.name;
    text = (focusPlanet.sub ? focusPlanet.sub + '. ' : '') + 'Companies (moons) and people of this planet.';
  }
  const ringPeople = r.people && universe.confirmed ? universe.people.filter((p) => p.circleRing === key) : null;

  // ----- pyramid info card -----
  const ptier = pyrFocus ? pyrFocus.tier : pos.tier;
  const plevel = pyrFocus ? pyrFocus.level : pos.level;
  const pyrMine = !pyrFocus || (pyrFocus.tier === pos.tier && pyrFocus.level === pos.level);

  // ----- sheet content -----
  let sheetEl = null;
  if (sheet) {
    const planet = planetById(sheet.planet);
    const company = sheet.company ? companyById(sheet.company) : null;
    const planetCompanies = universe.companies.filter((c) => c.planetId === sheet.planet);
    let visual = '', body = null;

    if (universe.loading && !universe.companies.length) {
      body = <div className="ct-sub">Loading the universe…</div>;
    } else if (universe.error && !universe.companies.length) {
      body = (
        <div className="ct-sub">
          Could not load companies. <button className="ct-linkbtn" onClick={loadUniverse}>Try again</button>
        </div>
      );
    } else if (!company) {
      visual = buildPlanetView(planet, planetCompanies, myCompanyIds, logos);
      const loose = universe.people.filter((p) => p.circleRing === 'planet' && p.circlePlanet === sheet.planet && !p.memberships.length);
      body = (
        <>
          <div className="ct-sheet-title">{planet.name}</div>
          <div className="ct-sub">Planet = industry. Moons = companies.{planetCompanies.length ? ' Tap a moon to see its departments and people.' : ''}</div>
          <div className="ct-list">
            {planetCompanies.map((c) => {
              const cnt = membersOf(c.companyId).length;
              return (
                <div key={c.companyId} className="ct-person clickable" role="button" tabIndex={0}
                  onClick={() => setSheet((s) => ({ ...s, company: c.companyId, openDept: null, highlight: null }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') setSheet((s) => ({ ...s, company: c.companyId, openDept: null, highlight: null })); }}>
                  <CompanyLogo companyId={c.companyId} />
                  <div className="ct-who">
                    <b>{c.name}{myCompanyIds.includes(c.companyId) && <span className="ct-you">YOU</span>}</b>
                    <small>{universe.confirmed ? `${cnt} ${cnt === 1 ? 'person' : 'people'}` : '7 departments'}</small>
                  </div>
                  <span className="ct-chev">›</span>
                </div>
              );
            })}
            {!planetCompanies.length && <div className="ct-sub">No companies on this planet yet.</div>}
          </div>
          {loose.length > 0 && (
            <>
              <div className="ct-subhead">Planet members without a company</div>
              {loose.map((p) => (
                <PersonRow key={p.genesisID} person={p} meId={meId} highlight={sheet.highlight === p.genesisID} onAction={personAction} />
              ))}
            </>
          )}
          {!universe.confirmed && <div className="ct-lock">People become visible after your status is confirmed.</div>}
          {note && <div className="ct-note">{note}</div>}
        </>
      );
    } else {
      const counts = {};
      DEPTS.forEach((d) => { counts[d.id] = membersOf(company.companyId, d.id).length; });
      visual = buildCompanyView(company, planet, counts, myDeptsIn(company.companyId), universe.confirmed, logos);
      body = (
        <>
          <div className="ct-sheet-title">{company.name}</div>
          <div className="ct-sub">{company.description || `Moon of ${planet.name}. Satellites = departments and the people in them.`}</div>
          {!universe.confirmed && <div className="ct-lock">People become visible after your status is confirmed.</div>}
          {DEPTS.map((d) => {
            const mem = membersOf(company.companyId, d.id);
            const open = sheet.openDept === d.id;
            const iAmHere = myDeptsIn(company.companyId).includes(d.id);
            return (
              <div className="ct-dept" key={d.id}>
                <button aria-expanded={open} onClick={() => setSheet((s) => ({ ...s, openDept: open ? null : d.id }))}>
                  <span className="ct-dot" style={{ background: d.color }} />
                  {d.name}
                  {d.note && <small className="ct-dept-note">{d.note}</small>}
                  {iAmHere && <span className="ct-you">YOU</span>}
                  <span className="ct-n">{universe.confirmed ? `${mem.length} ${open ? '▾' : '▸'}` : '🔒'}</span>
                </button>
                {open && universe.confirmed && (
                  <div className="ct-dept-inner">
                    {mem.length
                      ? mem.map((p) => (
                        <PersonRow key={p.genesisID} person={p} meId={meId} highlight={sheet.highlight === p.genesisID}
                          onAction={personAction}
                          subtitle={`Genesis ID ${p.genesisID}${p.memberships.length > 1 ? ` · ${p.memberships.length} companies` : ''}`} />
                      ))
                      : <div className="ct-empty">Nobody yet</div>}
                  </div>
                )}
              </div>
            );
          })}
          {note && <div className="ct-note">{note}</div>}
        </>
      );
    }

    sheetEl = (
      <>
        <div className="ct-backdrop" onClick={() => setSheet(null)} />
        <div className="ct-sheet" role="dialog" aria-modal="true" aria-label={planet.name}>
          <div className="ct-grab" />
          <div className="ct-sheet-head">
            <div className="ct-crumbs">
              {company ? (
                <>
                  <button onClick={() => setSheet((s) => ({ ...s, company: null, openDept: null, highlight: null }))}>{planet.name}</button>
                  <span>›</span>
                  <span className="cur">{company.name}</span>
                </>
              ) : (
                <span className="cur">{planet.name}</span>
              )}
            </div>
            <button className="ct-close" aria-label="Close" onClick={() => setSheet(null)}>×</button>
          </div>
          <div className="ct-sheet-body">
            {visual && <div className="ct-sheet-visual" onClick={onSheetVisual} onKeyDown={onSheetVisual} dangerouslySetInnerHTML={{ __html: visual }} />}
            <div className="ct-sheet-pad">{body}</div>
          </div>
        </div>
      </>
    );
  }

  return (
    <div className="ct-root">
      <div className="ct-section">
        <div className="ct-section-head"><h3>Social Circle</h3><span>Tap a ring or planet</span></div>

        <div className="ct-search">
          <input
            type="search"
            value={query}
            placeholder="Find a person, Genesis ID or company"
            aria-label="Search the THEO universe"
            onChange={(e) => { setQuery(e.target.value); setShowResults(true); }}
            onFocus={() => setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
          />
          {showResults && query.trim().length >= 2 && (
            <div className="ct-results">
              {results.length === 0 && (
                <div className="ct-empty">
                  {universe.loading ? 'Loading…' : `Nothing found${universe.confirmed ? '' : '. People search opens after your status is confirmed'}.`}
                </div>
              )}
              {results.map((res) => (
                <div key={res.key} className="ct-res" role="button" tabIndex={0}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pickResult(res)}
                  onKeyDown={(e) => { if (e.key === 'Enter') pickResult(res); }}>
                  {res.kind === 'company' ? <CompanyLogo companyId={res.company.companyId} /> : <Avatar person={res.person} size={32} />}
                  <div className="ct-who">
                    <b>{res.kind === 'company' ? res.company.name : res.person.name}</b>
                    <small>{resultPath(res)}</small>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="ct-universe">
          <svg viewBox="0 0 640 640" role="img" aria-label="Social Circle"
            onClick={onCircle} onKeyDown={onCircle}
            dangerouslySetInnerHTML={{ __html: circleHtml }} />
        </div>

        <div className="ct-info" aria-live="polite">
          <div className="ct-eyebrow">{(mine ? 'You are here · ' : '') + r.eyebrow}</div>
          <div className="ct-title">{title}</div>
          <div className="ct-text">{text}</div>
          <div className="ct-row">
            {focusPlanet && focusPlanet.rank > 0 && <span className="ct-chip">Priority #{focusPlanet.rank}</span>}
            {r.fact && <span className="ct-chip fact">Display only</span>}
            {!r.fact && r.unconfirmed && <span className="ct-chip warn">Not confirmed</span>}
            {!r.fact && !r.unconfirmed && mine && <span className="ct-chip ok">Confirmed</span>}
            {focusPlanet && <button className="ct-linkbtn" onClick={() => openPlanet(focusPlanet.id)}>Open planet ›</button>}
            {f && <button className="ct-linkbtn" onClick={() => { setCircleFocus(null); setNote(''); }}>Show my position</button>}
          </div>
          {ringPeople && (
            <div className="ct-people">
              {ringPeople.length
                ? ringPeople.map((p) => <PersonRow key={p.genesisID} person={p} meId={meId} onAction={personAction} />)
                : <div className="ct-empty">Nobody on this orbit yet</div>}
            </div>
          )}
          {r.people && !universe.confirmed && !universe.loading && (
            <div className="ct-lock">People become visible after your status is confirmed.</div>
          )}
          {note && !sheet && <div className="ct-note">{note}</div>}
        </div>
      </div>

      <div className="ct-section">
        <div className="ct-section-head"><h3>Level-Up Pyramid</h3><span>Tap a step</span></div>
        <div className="ct-universe ct-pyr-panel">
          <svg viewBox="0 0 400 390" role="img" aria-label="Level-Up Pyramid"
            onClick={onPyramid} onKeyDown={onPyramid}
            dangerouslySetInnerHTML={{ __html: pyramidHtml }} />
        </div>
        <div className="ct-info" aria-live="polite">
          <div className="ct-eyebrow">{pyrMine ? 'You are here' : 'Pyramid step'} · Step {(4 - ptier) * 4 + plevel} of 16</div>
          <div className="ct-title">Tier {ptier} · Level {plevel}</div>
          <div className="ct-text">Name and rules of this level come from the Level-Up algorithm (pending). For now the level is assigned manually.</div>
          <div className="ct-row">
            <span className="ct-chip">Placeholder name</span>
            {!pyrMine && <button className="ct-linkbtn" onClick={() => setPyrFocus(null)}>Show my position</button>}
          </div>
        </div>
      </div>

      {sheetEl}

      {personCard && (() => {
        const p = personCard.person;
        const isMe = String(p.genesisID) === String(meId);
        const ring = RINGS[p.circleRing] || RINGS.outer;
        const pl = planetById(p.circlePlanet);
        return (
          <>
            <div className="ct-backdrop ct-top" onClick={() => setPersonCard(null)} />
            <div className="ct-sheet ct-top" role="dialog" aria-modal="true" aria-label={p.name}>
              <div className="ct-grab" />
              <div className="ct-sheet-head">
                <div className="ct-crumbs"><span className="cur">Member profile</span></div>
                <button className="ct-close" aria-label="Close" onClick={() => setPersonCard(null)}>×</button>
              </div>
              <div className="ct-sheet-body">
                <div className="ct-sheet-pad">
                  <div className="ct-card-top">
                    <Avatar person={p} size={64} />
                    <div className="ct-who">
                      <div className="ct-sheet-title">{p.name}{isMe && <span className="ct-you">YOU</span>}</div>
                      <small>Genesis ID {p.genesisID}</small>
                    </div>
                  </div>
                  <div className="ct-row">
                    <span className={`ct-chip ${ring.unconfirmed ? 'warn' : 'ok'}`}>{p.circleRing === 'planet' && pl ? `Planet · ${pl.name}` : ring.title}</span>
                    {p.userRole && <span className="ct-chip">{p.userRole}</span>}
                  </div>

                  {!isMe && canSend && personCard.mode === null && (
                    <button className="ct-btn-primary" onClick={() => setPersonCard((c) => ({ ...c, mode: 'choose' }))}>Send Theo Asset</button>
                  )}

                  {!isMe && personCard.mode === 'choose' && (
                    <>
                      <div className="ct-subhead">Send to {p.name}</div>
                      {onSendToken && (
                        <div className="ct-token-grid">
                          {['SOL', 'THEO', 'USDC', 'USDT'].map((sym) => (
                            <button key={sym} onClick={() => sendToken(sym, p)}>{sym}</button>
                          ))}
                        </div>
                      )}
                      {onTransferAsset && (
                        <button className="ct-btn-secondary" onClick={() => setPersonCard((c) => ({ ...c, mode: 'asset' }))}>NFT / Genesis asset</button>
                      )}
                      <button className="ct-linkbtn" onClick={() => setPersonCard((c) => ({ ...c, mode: null }))}>Back</button>
                    </>
                  )}

                  {!isMe && personCard.mode === 'asset' && (
                    <>
                      <div className="ct-subhead">Choose an asset to send</div>
                      {assets.length ? assets.map((a) => (
                        <div className="ct-person clickable" key={a.assetId} role="button" tabIndex={0} onClick={() => sendAsset(a, p)}>
                          {a.imageUrl ? <img className="ct-asset-thumb" src={a.imageUrl} alt="" /> : <div className="ct-asset-thumb" />}
                          <div className="ct-who">
                            <b>{a.title || 'Genesis Asset'}</b>
                            <small>{a.nftMintAddress ? 'NFT' : 'Genesis asset'}{a.timestamp ? ` · ${new Date(a.timestamp).toLocaleDateString()}` : ''}</small>
                          </div>
                          <span className="ct-chev">›</span>
                        </div>
                      )) : <div className="ct-empty">You have no assets yet.</div>}
                      <button className="ct-linkbtn" onClick={() => setPersonCard((c) => ({ ...c, mode: 'choose' }))}>Back</button>
                    </>
                  )}
                  <div className="ct-subhead">In the TheoUniverse</div>
                  {p.memberships.length ? p.memberships.map((m) => {
                    const c = companyById(m.companyId);
                    return (
                      <div className="ct-person clickable" key={m.companyId + m.position} role="button" tabIndex={0}
                        onClick={() => { if (c) { setPersonCard(null); setSheet({ planet: c.planetId, company: c.companyId, openDept: m.position, highlight: p.genesisID }); } }}>
                        <CompanyLogo companyId={m.companyId} />
                        <div className="ct-who">
                          <b>{c ? c.name : m.companyId}</b>
                          <small>{planetById(c?.planetId)?.name || ''} › {deptById(m.position).name}</small>
                        </div>
                        <span className="ct-chev">›</span>
                      </div>
                    );
                  }) : <div className="ct-empty">Not placed in a company yet.</div>}

                </div>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  );
}

export default CircleTab;
